import {
	faCheck,
	faChevronDown,
	faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import type {
	FormatOptionLabelMeta,
	OptionProps,
	SelectComponentsConfig,
	SingleValueProps,
	StylesConfig,
} from 'react-select';
import Select, { components } from 'react-select';
import { useTheme, useThemeColors } from '../../providers/ThemeProvider';
import { formTheme } from '../../services/ThemeService';
import { cn } from '../../utils/cn';

export interface SingleSelectOption {
	value: string | number;
	label: string;
	disabled?: boolean;
}

interface SingleSelectProps {
	name: string;
	value?: string | number | null;
	options: SingleSelectOption[];
	onChange?: (value: string) => void;
	placeholder?: string;
	disabled?: boolean;
	error?: boolean;
	fullWidth?: boolean;
	size?: 'xs' | 'sm' | 'md' | 'lg';
	allowClear?: boolean;
	searchable?: boolean;
	className?: string;
	menuPortalTarget?: HTMLElement | null;
	formatOptionLabel?: (
		option: SingleSelectOption,
		labelMeta: FormatOptionLabelMeta<SingleSelectOption>,
	) => React.ReactNode;
	styles?: Partial<StylesConfig<SingleSelectOption, false>>;
	components?: Partial<SelectComponentsConfig<SingleSelectOption, false>>;
}

// Custom option component to show checkmark for selected option
const Option = (props: OptionProps<SingleSelectOption>) => {
	const colors = useThemeColors();
	return (
		<components.Option {...props}>
			<div className="flex items-center justify-between w-full">
				<span className={cn(props.isDisabled && 'opacity-50')}>
					{props.children}
				</span>
				{props.isSelected && (
					<FontAwesomeIcon
						icon={faCheck}
						className={cn('h-3 w-3', colors.classes.textPrimary)}
					/>
				)}
			</div>
		</components.Option>
	);
};

// Custom SingleValue component to match Select styling
const SingleValue = (props: SingleValueProps<SingleSelectOption>) => (
	<components.SingleValue {...props}>
		<span className="block truncate">{props.children}</span>
	</components.SingleValue>
);

// Custom dropdown indicator to match Select's chevron
const DropdownIndicator = (props: any) => {
	const colors = useThemeColors();
	return (
		<components.DropdownIndicator {...props}>
			<FontAwesomeIcon
				icon={faChevronDown}
				className={cn('h-3 w-3', colors.classes.textSecondary)}
			/>
		</components.DropdownIndicator>
	);
};

// Custom clear indicator — matches BaseTextInput's X: just a color
// transition on the icon, no background, cursor-pointer.
const ClearIndicator = (props: any) => {
	const colors = useThemeColors();
	return (
		<components.ClearIndicator {...props}>
			<div className="cursor-pointer">
				<FontAwesomeIcon
					icon={faXmark}
					className={cn(
						'h-3 w-3',
						colors.classes.textSecondary,
						'hover:' + colors.classes.textPrimary,
						'transition-colors',
					)}
				/>
			</div>
		</components.ClearIndicator>
	);
};

export function SingleSelect({
	name,
	value,
	options,
	onChange,
	placeholder = 'Select...',
	disabled = false,
	error = false,
	fullWidth = true,
	size = 'md',
	allowClear = false,
	searchable = false,
	className,
	menuPortalTarget = null,
	formatOptionLabel,
	styles: customStylesOverride,
	components: customComponents,
}: SingleSelectProps) {
	const colors = useThemeColors();
	const { colorScheme } = useTheme();
	const formColors = formTheme.values[colorScheme];

	// Size configurations matching Select component exactly
	const sizeConfig = {
		xs: {
			minHeight: '32px',
			fontSize: '0.75rem', // text-xs
			controlPadding: '0 0.375rem', // px-1.5
			optionPadding: '0.5rem 0.75rem', // py-2 px-3
		},
		sm: {
			minHeight: '30px',
			fontSize: '0.875rem', // text-sm
			controlPadding: '0 0.625rem', // px-2.5
			optionPadding: '0.5rem 0.75rem',
		},
		md: {
			minHeight: '36px',
			fontSize: '0.875rem', // text-sm
			controlPadding: '0 0.75rem', // px-3
			optionPadding: '0.5rem 0.75rem',
		},
		lg: {
			minHeight: '42px',
			fontSize: '1rem', // text-base
			controlPadding: '0 1rem', // px-4
			optionPadding: '0.5rem 0.75rem',
		},
	};

	const currentSize = sizeConfig[size];

	// Find the selected option
	const selectedOption = options.find(
		(opt) =>
			opt.value === value ||
			(value == null && opt.value === '') ||
			String(opt.value) === String(value),
	);

	// Theme-aware styles matching Select component exactly
	const customStyles: StylesConfig<SingleSelectOption, false> = {
		control: (base, state) => ({
			...base,
			minHeight: currentSize.minHeight,
			fontSize: currentSize.fontSize,
			backgroundColor: state.isDisabled
				? formColors.backgroundDisabled
				: formColors.background,
			borderColor: state.isDisabled
				? formColors.borderDisabled
				: error
					? colors.values.danger
					: state.isFocused
						? formColors.borderFocus
						: formColors.border,
			borderRadius: '0.375rem',
			borderWidth: '1px',
			boxShadow:
				state.isFocused && !error
					? `0 0 0 3px ${colors.values.primary}1a`
					: 'none',
			'&:hover': {
				borderColor: state.isFocused
					? formColors.borderFocus
					: formColors.borderHover,
			},
			cursor: state.isDisabled ? 'not-allowed' : 'pointer',
			padding: currentSize.controlPadding,
		}),
		valueContainer: (base) => ({
			...base,
			padding: '0.5rem 0',
			paddingRight: allowClear ? '0' : '0.5rem',
		}),
		singleValue: (base) => ({
			...base,
			color: formColors.text,
			marginLeft: 0,
			marginRight: 0,
		}),
		placeholder: (base) => ({
			...base,
			color: formColors.textPlaceholder,
			marginLeft: 0,
			marginRight: 0,
		}),
		input: (base) => ({
			...base,
			color: formColors.text,
			margin: 0,
			padding: 0,
		}),
		menu: (base) => ({
			...base,
			backgroundColor: formColors.menuBackground,
			borderRadius: '0.375rem',
			boxShadow:
				'0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
			border: `1px solid ${formColors.border}`,
			marginTop: '0.25rem',
			zIndex: 9999,
		}),
		menuPortal: (base) => ({
			...base,
			zIndex: 9999,
		}),
		option: (base, state) => ({
			...base,
			backgroundColor: state.isSelected
				? formColors.optionSelected
				: state.isFocused
					? formColors.optionHover
					: 'transparent',
			color: state.isSelected ? formColors.optionSelectedText : formColors.text,
			padding: currentSize.optionPadding,
			fontSize: currentSize.fontSize,
			cursor: state.isDisabled ? 'not-allowed' : 'pointer',
			':active': {
				backgroundColor: formColors.optionHover,
			},
		}),
		indicatorSeparator: () => ({
			display: 'none',
		}),
		dropdownIndicator: (base, state) => ({
			...base,
			padding: '0 0.5rem',
			color: colors.values.textSecondary,
			'&:hover': {
				color: colors.values.textPrimary,
			},
		}),
		clearIndicator: (base) => ({
			...base,
			padding: '0 0.25rem',
			cursor: 'pointer',
			color: colors.values.textSecondary,
			'&:hover': {
				color: colors.values.textPrimary,
			},
		}),
		loadingIndicator: (base) => ({
			...base,
			color: formColors.text,
		}),
		noOptionsMessage: (base) => ({
			...base,
			color: formColors.textPlaceholder,
			padding: currentSize.optionPadding,
			fontSize: currentSize.fontSize,
		}),
	};

	const handleChange = (newValue: SingleSelectOption | null) => {
		if (onChange) {
			onChange(newValue ? String(newValue.value) : '');
		}
	};

	const mergedStyles =
		customStylesOverride != null
			? Object.keys(customStyles).reduce<
					StylesConfig<SingleSelectOption, false>
				>((acc, key) => {
					const styleKey = key as keyof StylesConfig<SingleSelectOption, false>;
					const baseStyle = customStyles[styleKey];
					const overrideStyle = customStylesOverride[styleKey];

					if (overrideStyle != null && baseStyle != null) {
						acc[styleKey] = (base: any, state: any) => ({
							...(typeof baseStyle === 'function'
								? baseStyle(base, state)
								: baseStyle),
							...(typeof overrideStyle === 'function'
								? overrideStyle(base, state)
								: overrideStyle),
						});
					} else {
						acc[styleKey] = baseStyle ?? overrideStyle;
					}

					return acc;
				}, {})
			: customStyles;

	const mergedComponents = {
		Option,
		SingleValue,
		DropdownIndicator,
		ClearIndicator,
		IndicatorSeparator: () => null,
		...(customComponents || {}),
	};

	return (
		<Select<SingleSelectOption>
			instanceId={`${name}-select`}
			name={name}
			value={selectedOption || null}
			onChange={handleChange}
			options={options}
			placeholder={placeholder}
			isDisabled={disabled}
			isClearable={allowClear}
			isSearchable={searchable}
			isOptionDisabled={(option) => !!option.disabled}
			className={cn('react-select-container', fullWidth && 'w-full', className)}
			classNamePrefix="react-select"
			styles={mergedStyles}
			components={mergedComponents}
			formatOptionLabel={formatOptionLabel}
			menuPortalTarget={menuPortalTarget}
			menuPlacement="auto"
			menuPosition="fixed"
			inputProps={{ autoComplete: 'off' }}
		/>
	);
}
